import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  IconButton,
  Divider,
  Menu,
  //Tooltip,
  InputAdornment,
  //IconButton as MuiIconButton,
  //MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  //ArrowLeft,
  Edit,
  Save,
  FileText,
  CheckCircle,
  Calendar,
  DollarSign,
  AppWindowIcon as AppsIcon,
  CloudIcon,
  CodeIcon,
  SheetIcon,
  //Send as SendIcon,
  //GitBranch,
  //AlertTriangle,
  //Users,
  Building,
  Users as PeopleIcon,
  FileCheck as SolutionsIcon,
  DoorClosed,
} from 'lucide-react';
import {
  InsertDriveFile as DocIcon,
  Business as BusinessIcon,
  Slideshow as SlideIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import GradientText from '../common/GradientText.tsx';
import DocumentTools from '../workspace/DocumentTools.tsx';
import ProblemSourcingDrawer from '../workspace/ProblemSourcingDrawer.tsx';
import PlanStepper from './PlanStepper.tsx';
import EditableContent from './EditableContent.tsx';
//import SavvyDataProposalCard from '../savvydata/SavvyDataProposalCard.tsx';
//import { proposals } from '../../data/mockData.ts';
import BackButton from '../common/BackButton.tsx';
//import ProposalCard from '../../pages/Landing/components/ProposalCard.tsx';
//import EditableProposalCard from '../workspace/EditableProposalCard.tsx';
import ExecutiveStepper from '../executives/ExecutiveStepper.tsx';

// Update/enhance the ProposalData interface to match what's in ProblemWorkspace
interface ProposalData {
  id: string;
  title: string;
  description: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  estimatedTimeline?: string;
  timelineType?: string;
  status: 'draft' | 'submitted' | 'in-review' | 'approved' | 'rejected';
  cost: number;
  amount: number;
  priority?: 'Low' | 'Medium' | 'High';
  riskLevel?: 'Low' | 'Medium' | 'High';
}

interface BlankProposalTemplateProps {
  proposalId: string;
  initialTitle: string;
  initialDescription: string;
  initialContent: string;
  amount?: number;
  status?: string;
  onSave: (proposal: ProposalData) => void;
  onBack: () => void;
}

const ProposalContainer = styled(Paper)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  position: 'relative',
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.5)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.15)',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: 'rgba(0, 255, 255, 0.7)',
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.1)',
    color: '#fff',
  },
}));

//const GradientButton = styled(Button)(({ theme }) => ({
  //background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
  //color: '#fff',
  //'&:hover': {
  //  background: 'linear-gradient(45deg, #00CC99 40%, #8A2BE2 100%)',
  //  boxShadow: '0 0 10px rgba(0, 204, 153, 0.5)',
  //},
//}));

const BentoGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const BentoItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.3)',
    border: '1px solid rgba(138, 43, 226, 0.5)',
  },
}));

const bentoOptions = [
  { id: 'plan', label: 'Plan', icon: <BuildIcon /> },
  { id: 'write', label: 'Write', icon: <DocIcon /> },
  { id: 'executives', label: 'Executives', icon: <PeopleIcon /> },
  { id: 'salesforce', label: 'Salesforce', icon: <BusinessIcon /> },
  { id: 'chrome', label: 'Browse', icon: <CloudIcon /> },
  { id: 'api', label: 'API', icon: <CodeIcon /> },
  { id: 'sheet', label: 'Sheet', icon: <SheetIcon /> },
  { id: 'slides', label: 'Slides', icon: <SlideIcon /> },
  { id: 'solutions', label: 'Solutions', icon: <SolutionsIcon /> }
];

const BlankProposalTemplate: React.FC<BlankProposalTemplateProps> = ({
  proposalId,
  initialTitle,
  initialDescription,
  initialContent,
  amount = 0,
  status = 'draft',
  onSave,
  onBack
}) => {
  // Initialize the proposal state using props
  const [proposal, setProposal] = useState<ProposalData>({
    id: proposalId,
    title: initialTitle,
    description: initialDescription,
    content: initialContent,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: status as ProposalData['status'],
    cost: amount,
    amount: amount,
    priority: 'Medium',
    riskLevel: 'Low',
  });

  // Update proposal state if props change
  useEffect(() => {
    setProposal(prev => ({
      ...prev,
      id: proposalId,
      title: initialTitle,
      description: initialDescription,
      content: initialContent,
      status: status as ProposalData['status'],
      cost: amount,
      amount: amount,
    }));
  }, [proposalId, initialTitle, initialDescription, initialContent, status, amount]);

  // Handler for updating the proposal state
  const handleProposalUpdate = (updatedProposal: Partial<ProposalData>) => {
    setProposal(prev => {
      // If amount is being updated, also update cost to match
      if (updatedProposal.amount !== undefined && updatedProposal.cost === undefined) {
        updatedProposal.cost = updatedProposal.amount;
      }
      
      const updated = {
        ...prev,
        ...updatedProposal,
        updatedAt: new Date().toISOString()
      };
      
      // Call the parent's onSave to sync changes
      onSave(updated);
      
      return updated;
    });
  };

  // UI state variables
  const [isEditingContent, setIsEditingContent] = useState(false);
  
  // Additional state for specialized displays
  const [showDocumentTools, setShowDocumentTools] = useState(false);
  const [documentToolType, setDocumentToolType] = useState<'document' | 'spreadsheet'>('document');
  const [showPlanStepper, setShowPlanStepper] = useState(false);
  const [showExecutiveStepper, setShowExecutiveStepper] = useState(false);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [bentoAnchorEl, setBentoAnchorEl] = useState<null | HTMLElement>(null);
  const [showProblemSourcingDrawer, setShowProblemSourcingDrawer] = useState(false);
  const [executives] = useState([]); // This would be populated from your data source
  
  // Initialize all the different sections of the proposal
  const [companyInfo, setCompanyInfo] = useState({
    name: 'Your Company',
    description: 'A brief description of your company...',
    vision: 'Your company vision statement...',
    mission: 'Your company mission statement...'
  });
  
  const [solutionInfo, setSolutionInfo] = useState({
    title: 'Proposed Solution',
    description: 'A detailed description of your proposed solution...',
    cost: '$0',
    timeframe: '0 months'
  });
  
  const [objectives, setObjectives] = useState([
    {
      id: '1',
      title: 'Business Objective',
      description: 'Description of your primary business objective',
      keyResults: [
        {
          id: '1-1',
          description: 'Key result 1',
          progress: 0,
          target: '0/100 complete',
        }
      ]
    }
  ]);
  
  const [processInfo] = useState({
    currentTitle: 'Current Process',
    currentDescription: 'Description of the current process...',
    currentSteps: [
      { id: '1', description: 'Step 1 of current process' }
    ],
    futureTitle: 'Future Process',
    futureDescription: 'Description of the improved future process...',
    futureSteps: [
      { id: '1', description: 'Step 1 of improved process' }
    ]
  });
  
  const [financialItems] = useState({
    currentItems: [
      {
        id: '1',
        description: 'Current cost or risk',
        amount: -10000,
        type: 'risk'
      }
    ],
    futureItems: [
      {
        id: '1',
        description: 'Future profit or benefit',
        amount: 20000,
        type: 'profit'
      }
    ]
  });
  
  const [useCases] = useState({
    currentUseCases: [
      {
        id: '1',
        title: 'Current Use Case',
        description: 'Description of current use case...'
      }
    ],
    futureUseCases: [
      {
        id: '1',
        title: 'Future Use Case',
        description: 'Description of improved future use case...'
      }
    ]
  });

  // Handle updates to proposal sections
  const updateCompanyInfo = (newInfo: any) => {
    setCompanyInfo(newInfo);
    handleProposalUpdate({ 
      content: proposal.content + `\n\n## Company Information\n${JSON.stringify(newInfo, null, 2)}`
    });
  };
  
  const updateSolutionInfo = (newInfo: any) => {
    setSolutionInfo(newInfo);
    handleProposalUpdate({ 
      content: proposal.content + `\n\n## Solution Information\n${JSON.stringify(newInfo, null, 2)}`
    });
  };
  
  const updateObjectives = (newObjectives: any) => {
    setObjectives(newObjectives);
    handleProposalUpdate({ 
      content: proposal.content + `\n\n## Objectives\n${JSON.stringify(newObjectives, null, 2)}`
    });
  };
  
  // Other handlers
  const handleBentoClick = (event: React.MouseEvent<HTMLElement>) => {
    setBentoAnchorEl(event.currentTarget);
  };

  const handleBentoClose = () => {
    setBentoAnchorEl(null);
  };

  const handleBentoSelect = (optionId: string) => {
    // Reset all tool states first
    setShowDocumentTools(false);
    setShowPlanStepper(false);
    setShowExecutiveStepper(false);
    setSelectedTool(null);
    
    // Then set the appropriate state based on selection
    if (optionId === 'write') {
      setDocumentToolType('document');
      setShowDocumentTools(true);
    } else if (optionId === 'sheet') {
      setDocumentToolType('spreadsheet');
      setShowDocumentTools(true);
    } else if (optionId === 'plan') {
      setShowPlanStepper(true);
      setSelectedTool('plan');
    } else if (optionId === 'executives') {
      setShowExecutiveStepper(true);
      setSelectedTool('executives');
    }
    handleBentoClose();
  };

  // Add a handler for generating content
  const handleGenerate = (section: string) => {
    // In a real implementation, this would call your AI service to generate content
    console.log(`Generating content for ${section}`);
    // For now, we'll just add some placeholder text
    const placeholderContent = `[AI Generated content for ${section} would appear here]`;
    
    // Update the appropriate section based on which one requested generation
    switch(section) {
      case 'solution':
        updateSolutionInfo({...solutionInfo, description: solutionInfo.description + '\n\n' + placeholderContent});
        break;
      case 'companyDescription':
        updateCompanyInfo({...companyInfo, description: companyInfo.description + '\n\n' + placeholderContent});
        break;
      case 'vision':
        updateCompanyInfo({...companyInfo, vision: companyInfo.vision + '\n\n' + placeholderContent});
        break;
      case 'mission':
        updateCompanyInfo({...companyInfo, mission: companyInfo.mission + '\n\n' + placeholderContent});
        break;
      default: {
        // Update general proposal content
        const updatedProposal = {
          ...proposal,
          content: proposal.content + '\n\n' + placeholderContent,
          updatedAt: new Date().toISOString()
        };
        setProposal(updatedProposal);
      }
    }
    
    // Show a success message
    console.log(`Content generated for ${section}`);
  };

  // Save handler for different proposal components
  const handleSaveDocumentContent = (content: string) => {
    handleProposalUpdate({ content });
    setShowDocumentTools(false);
  };

  const handleSaveProposal = () => {
    // Simply call the parent's onSave with the current proposal state
    onSave(proposal);
    setIsEditingContent(false);
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <BackButton onClick={onBack} />
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<Save />}
          onClick={handleSaveProposal}
        >
          Save Proposal
        </Button>
      </Box>
      
      <ProposalContainer>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              PROPOSAL ID: {proposalId}
            </Typography>
          </Box>
          {isEditingContent ? (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Title"
                value={proposal.title}
                onChange={(e) => handleProposalUpdate({ title: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Description"
                value={proposal.description}
                onChange={(e) => handleProposalUpdate({ description: e.target.value })}
                multiline
                rows={3}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  onClick={() => setIsEditingContent(false)}
                >
                  Save
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <GradientText variant="h4">{proposal.title}</GradientText>
                <StyledIconButton onClick={() => setIsEditingContent(true)}>
                  <Edit />
                </StyledIconButton>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {proposal.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Calendar size={16} />
                  <Typography variant="body2" color="text.secondary">
                    {new Date(proposal.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Calendar size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Timeline: {proposal.estimatedTimeline} months
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DollarSign size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Estimated Cost: ${proposal.cost}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle size={16} />
                  <Typography variant="body2" sx={{ 
                    color: '#00CC99',
                    background: 'rgba(0, 204, 153, 0.1)',
                    padding: '2px 8px',
                    borderRadius: '4px'
                  }}>
                    {proposal.status}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <SectionContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <GradientText gradient="secondary" variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
            {selectedTool ? `CURRENT: ${selectedTool.toUpperCase()}` : 'SELECT A TOOL TO GET STARTED =>'}
          </GradientText>
          <Box>
            <StyledIconButton
              onClick={handleBentoClick}
              sx={{ color: 'rgba(0, 255, 255, 0.7)' }}
            >
              <AppsIcon />
            </StyledIconButton>
          </Box>
        </Box>

        <Menu
          anchorEl={bentoAnchorEl}
          open={Boolean(bentoAnchorEl)}
          onClose={handleBentoClose}
          PaperProps={{
            sx: { width: 320, maxWidth: '100%', bgcolor: 'rgba(26, 26, 46, 0.9)' }
          }}
        >
          <BentoGrid container spacing={2}>
            {bentoOptions.map((option) => (
              <Grid item xs={4} key={option.id}>
                <BentoItem 
                  onClick={() => handleBentoSelect(option.id)}
                  sx={{
                    border: selectedTool === option.id ? '2px solid #00CC99' : undefined,
                    background: selectedTool === option.id ? 'rgba(0, 204, 153, 0.1)' : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {option.icon}
                    <Typography variant="body2" sx={{ mt: 1, color: selectedTool === option.id ? '#00CC99' : '#fff' }}>
                      {option.label}
                    </Typography>
                  </Box>
                </BentoItem>
              </Grid>
            ))}
          </BentoGrid>
        </Menu>

        {showDocumentTools && (
          <Box sx={{ mt: 2, position: 'relative' }}>
            <Box sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8, 
              zIndex: 1
            }}>
              <IconButton 
                onClick={() => {
                  setShowDocumentTools(false);
                  setSelectedTool(null);
                }}
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: '#fff',
                    backgroundColor: 'rgba(255, 0, 0, 0.1)',
                  }
                }}
              >
                <DoorClosed size={20} />
              </IconButton>
            </Box>
            <DocumentTools
              initialTab={documentToolType === 'document' ? 0 : 1}
              problemText={proposal.description}
              setProblemText={(text) => handleProposalUpdate({ description: text })}
              onSaveDocument={handleSaveDocumentContent}
              onAddDocumentToProposal={(content) => {
                handleProposalUpdate({ content });
              }}
            />
          </Box>
        )}
        </SectionContainer>

        <Divider sx={{ mb: 2 }} />
        
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3, fontStyle: 'italic' }}>
          This is a draft proposal. Add more sections and collaborate with team members to finalize it.
        </Typography>
        
        {/* Proposal Content - Structured similar to ProposalDetail */}
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <SectionContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Proposed Solution</Typography>
                <StyledIconButton onClick={() => {
                  // Add document generation functionality here
                  setShowDocumentTools(true);
                  setDocumentToolType('document');
                }}>
                  <Edit />
                </StyledIconButton>
              </Box>
              <EditableContent
                content={solutionInfo.description}
                title="Solution Overview"
                icon={SolutionsIcon}
                onUpdate={(newData) => updateSolutionInfo({
                  ...solutionInfo,
                  description: newData.content || ""
                })}
                onGenerate={() => handleGenerate('solution')}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <TextField
                  label="Estimated Cost"
                  value={solutionInfo.cost}
                  onChange={(e) => updateSolutionInfo({...solutionInfo, cost: e.target.value})}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ width: '48%' }}
                />
                <TextField
                  label="Timeframe"
                  value={solutionInfo.timeframe}
                  onChange={(e) => updateSolutionInfo({...solutionInfo, timeframe: e.target.value})}
                  variant="outlined"
                  size="small"
                  sx={{ width: '48%' }}
                />
              </Box>
              
              <Button
                variant="outlined"
                sx={{ mt: 2 }}
                startIcon={<FileText />}
                onClick={() => {
                  // Link to solution detail or create new solution functionality
                }}
              >
                View Full Solution
              </Button>
            </SectionContainer>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <SectionContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Company Description</Typography>
                <StyledIconButton onClick={() => {
                  // Add editing functionality here
                }}>
                  <Edit />
                </StyledIconButton>
              </Box>
              <EditableContent
                content={companyInfo.description}
                title="About the Company"
                icon={Building}
                onUpdate={(newData) => updateCompanyInfo({
                  ...companyInfo,
                  description: newData.content || ""
                })}
                onGenerate={() => handleGenerate('companyDescription')}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Vision</Typography>
                <EditableContent
                  content={companyInfo.vision}
                  title="Vision"
                  onUpdate={(newData) => updateCompanyInfo({
                    ...companyInfo,
                    vision: newData.content || ""
                  })}
                  onGenerate={() => handleGenerate('vision')}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Mission</Typography>
                <EditableContent
                  content={companyInfo.mission}
                  title="Mission"
                  onUpdate={(newData) => updateCompanyInfo({
                    ...companyInfo,
                    mission: newData.content || ""
                  })}
                  onGenerate={() => handleGenerate('mission')}
                />
              </Box>
            </SectionContainer>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <SectionContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Objectives & Key Results</Typography>
                <StyledIconButton onClick={() => {
                  // Add editing functionality here
                }}>
                  <Edit />
                </StyledIconButton>
              </Box>
              {objectives.map((objective, index) => (
                <Box key={objective.id} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">{objective.title}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                    {objective.description}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Key Results:</Typography>
                  {objective.keyResults.map((kr) => (
                    <Box key={kr.id} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CheckCircle size={16} style={{ marginRight: 8, color: kr.progress > 0 ? '#00CC99' : '#8A2BE2' }} />
                      <Typography variant="body2">{kr.description}</Typography>
                    </Box>
                  ))}
                </Box>
              ))}
              <Button
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={() => {
                  // Add functionality to add new objectives
                  const newObjective = {
                    id: `obj-${Date.now()}`,
                    title: 'New Objective',
                    description: 'Description of this objective...',
                    keyResults: [
                      {
                        id: `kr-${Date.now()}`,
                        description: 'Key result',
                        progress: 0,
                        target: '0/100 complete',
                      }
                    ]
                  };
                  updateObjectives([...objectives, newObjective]);
                }}
              >
                Add Objective
              </Button>
            </SectionContainer>
          </Grid>
          
          <Grid item xs={12}>
            <SectionContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Process Comparison</Typography>
                <StyledIconButton onClick={() => {
                  // Add editing functionality here
                }}>
                  <Edit />
                </StyledIconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1">{processInfo.currentTitle}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                    {processInfo.currentDescription}
                  </Typography>
                  {processInfo.currentSteps.map((step, index) => (
                    <Box key={step.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ 
                        background: 'rgba(138, 43, 226, 0.2)', 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mr: 1
                      }}>
                        {index + 1}
                      </Typography>
                      <Typography variant="body2">{step.description}</Typography>
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1">{processInfo.futureTitle}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                    {processInfo.futureDescription}
                  </Typography>
                  {processInfo.futureSteps.map((step, index) => (
                    <Box key={step.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ 
                        background: 'rgba(0, 204, 153, 0.2)', 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mr: 1
                      }}>
                        {index + 1}
                      </Typography>
                      <Typography variant="body2">{step.description}</Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </SectionContainer>
          </Grid>
          
          <Grid item xs={12}>
            <SectionContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Profit/Risk Analysis</Typography>
                <StyledIconButton onClick={() => {
                  // Add editing functionality here
                }}>
                  <Edit />
                </StyledIconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Current Financial Situation</Typography>
                  {financialItems.currentItems.map((item) => (
                    <Box key={item.id} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      background: 'rgba(255, 76, 76, 0.1)'
                    }}>
                      <Typography variant="body2">{item.description}</Typography>
                      <Typography variant="body2" sx={{ color: '#FF4C4C' }}>
                        -${Math.abs(item.amount).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Projected Financial Impact</Typography>
                  {financialItems.futureItems.map((item) => (
                    <Box key={item.id} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      background: 'rgba(0, 204, 153, 0.1)'
                    }}>
                      <Typography variant="body2">{item.description}</Typography>
                      <Typography variant="body2" sx={{ color: '#00CC99' }}>
                        +${Math.abs(item.amount).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </SectionContainer>
          </Grid>
          
          <Grid item xs={12}>
            <SectionContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Use Case Comparison</Typography>
                <StyledIconButton onClick={() => {
                  // Add editing functionality here
                }}>
                  <Edit />
                </StyledIconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Current Use Cases</Typography>
                  {useCases.currentUseCases.map((useCase) => (
                    <Box key={useCase.id} sx={{ 
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid rgba(138, 43, 226, 0.2)',
                    }}>
                      <Typography variant="subtitle2">{useCase.title}</Typography>
                      <Typography variant="body2">{useCase.description}</Typography>
                    </Box>
                  ))}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Future Use Cases</Typography>
                  {useCases.futureUseCases.map((useCase) => (
                    <Box key={useCase.id} sx={{ 
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid rgba(0, 204, 153, 0.2)',
                    }}>
                      <Typography variant="subtitle2">{useCase.title}</Typography>
                      <Typography variant="body2">{useCase.description}</Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </SectionContainer>
          </Grid>
        </Grid>
        
        {/* Bento Menu for selecting tools */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Tools</Typography>
          <BentoGrid container spacing={2}>
            {bentoOptions.map((option) => (
              <Grid item xs={4} sm={4} md={4} lg={4} key={option.id}>
                <BentoItem 
                  onClick={() => {
                    setSelectedTool(option.id);
                    setBentoAnchorEl(null);
                    
                    if (option.id === 'plan') {
                      setShowPlanStepper(true);
                    } else if (option.id === 'executives') {
                      setShowExecutiveStepper(true);
                    } else if (option.id === 'write' || option.id === 'sheet') {
                      setDocumentToolType(option.id === 'write' ? 'document' : 'spreadsheet');
                      setShowDocumentTools(true);
                    }
                  }}
                  sx={{
                    borderColor: selectedTool === option.id ? 'rgba(138, 43, 226, 0.8)' : 'rgba(138, 43, 226, 0.2)',
                    background: selectedTool === option.id ? 'rgba(138, 43, 226, 0.15)' : 'rgba(26, 26, 46, 0.7)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    {React.cloneElement(option.icon as React.ReactElement, { 
                      style: { 
                        color: selectedTool === option.id ? '#8A2BE2' : 'rgba(255, 255, 255, 0.7)',
                        fontSize: '28px' 
                      } 
                    })}
                    <Typography variant="body2" sx={{ 
                      color: selectedTool === option.id ? '#fff' : 'rgba(255, 255, 255, 0.7)'
                    }}>
                      {option.label}
                    </Typography>
                  </Box>
                </BentoItem>
              </Grid>
            ))}
          </BentoGrid>
        </Box>
        
        {showDocumentTools && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Document Tools</Typography>
              <IconButton onClick={() => setShowDocumentTools(false)}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Close</Typography>
              </IconButton>
            </Box>
            <DocumentTools 
              initialTab={documentToolType === 'document' ? 0 : 1}
              initialDocumentContent=""
              onAddDocumentToProposal={(content) => {
                const updatedProposal = { ...proposal, content: proposal.content + '\n\n' + content };
                setProposal(updatedProposal);
              }}
              onSaveDocument={(content) => {
                const updatedProposal = { ...proposal, content: proposal.content + '\n\n' + content };
                setProposal(updatedProposal);
              }}
              onClose={() => setShowDocumentTools(false)}
            />
          </Box>
        )}
        
        {showProblemSourcingDrawer && (
          <ProblemSourcingDrawer 
            onSubmit={(problem) => {
              console.log("Selected problem:", problem);
              setShowProblemSourcingDrawer(false);
              
              // Add the selected problem to the proposal
              const updatedProposal = { 
                ...proposal, 
                description: proposal.description + '\n\nProblem Statement: ' + problem
              };
              setProposal(updatedProposal);
            }}
            onClose={() => setShowProblemSourcingDrawer(false)}
          />
        )}
        
        {showPlanStepper && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.8)', zIndex: 1300, p: 4 }}>
            <Box sx={{ 
              position: 'relative',
              width: '100%',
              height: '100%',
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 3,
              overflow: 'auto'
            }}>
              <IconButton 
                onClick={() => setShowPlanStepper(false)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <Typography variant="body2">Close</Typography>
              </IconButton>
              <PlanStepper 
                onComplete={() => {
                  console.log("Plan complete:");
                  setShowPlanStepper(false);
                  
                  // Update the proposal with the plan data
                  const updatedProposal = { 
                    ...proposal,
                    content: proposal.content + '\n\n## Strategic Plan Completed'
                  };
                  setProposal(updatedProposal);
                }}
              />
            </Box>
          </Box>
        )}
        
        {showExecutiveStepper && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.8)', zIndex: 1300, p: 4 }}>
            <Box sx={{ 
              position: 'relative',
              width: '100%',
              height: '100%',
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 3,
              overflow: 'auto'
            }}>
              <IconButton 
                onClick={() => setShowExecutiveStepper(false)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <Typography variant="body2">Close</Typography>
              </IconButton>
              <ExecutiveStepper 
                executives={executives}
                onComplete={() => {
                  console.log("Executive consultation complete");
                  setShowExecutiveStepper(false);
                  
                  // Update the proposal with executive insights
                  const updatedProposal = { 
                    ...proposal,
                    content: proposal.content + '\n\n## Executive Consultation Completed'
                  };
                  setProposal(updatedProposal);
                }}
                onBack={() => setShowExecutiveStepper(false)}
              />
            </Box>
          </Box>
        )}
        
      </ProposalContainer>
    </Box>
  );
};

export default BlankProposalTemplate; 